import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { API } from "@/lib/api";

const ATUALIZADO_EM = "8 de outubro de 2026";

function useEmpresa() {
  const [empresa, setEmpresa] = useState({ nome: "", documento: "", email_contato: "", cidade_uf: "" });
  useEffect(() => {
    axios.get(`${API}/publico/empresa`).then((r) => setEmpresa(r.data)).catch(() => {});
  }, []);
  return empresa;
}

function Layout({ titulo, children }) {
  useEffect(() => {
    document.title = `${titulo} | Aesthetica Pricing Pro`;
    window.scrollTo(0, 0);
  }, [titulo]);
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 text-slate-800">
      <div className="mx-auto max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-teal-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <h1 className="mt-4 text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{titulo}</h1>
        <p className="mt-1 text-sm text-slate-500">Atualizado em {ATUALIZADO_EM}</p>
        <article className="legal mt-8 space-y-6 rounded-xl bg-white p-6 leading-relaxed shadow-sm md:p-10">{children}</article>
      </div>
    </div>
  );
}

function S({ t, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">{t}</h2>
      {children}
    </section>
  );
}

function Quem({ empresa }) {
  const nome = empresa.nome || "Aesthetica Pricing Pro";
  return (
    <>
      <strong>{nome}</strong>
      {empresa.documento ? `, inscrita no CNPJ/CPF ${empresa.documento}` : ""}
    </>
  );
}

function Contato({ empresa }) {
  return empresa.email_contato ? (
    <a className="text-teal-700 underline" href={`mailto:${empresa.email_contato}`}>{empresa.email_contato}</a>
  ) : (
    <span>o canal de suporte informado no app</span>
  );
}

export function TermosPage() {
  const empresa = useEmpresa();
  return (
    <Layout titulo="Termos de Uso">
      <S t="1. Aceitação">
        <p>
          Estes Termos regem o uso do Aesthetica Pricing Pro (o “App”), oferecido por <Quem empresa={empresa} />. Ao
          criar uma conta ou assinar um plano, você declara que leu e concorda com estes Termos e com a{" "}
          <Link to="/privacidade" className="text-teal-700 underline">Política de Privacidade</Link>.
        </p>
      </S>
      <S t="2. O serviço">
        <p>
          O App é uma ferramenta on-line de apoio à precificação para clínicas e profissionais de estética: cadastro de
          produtos e insumos, fichas técnicas, cálculo de custos, margem e preço, relatórios, comparativos e exportação
          de dados. Os resultados dependem das informações que você cadastra. O App não substitui orientação contábil,
          tributária ou jurídica, e as decisões de preço são suas.
        </p>
      </S>
      <S t="3. Conta e segurança">
        <p>
          Você deve informar dados verdadeiros, ser maior de 18 anos e manter sua senha em sigilo. A conta é pessoal.
          Recomendamos ativar a verificação em duas etapas, disponível em Minha conta. Avise-nos imediatamente se
          suspeitar de uso indevido.
        </p>
      </S>
      <S t="4. Planos, pagamento e renovação automática">
        <p>
          O acesso é liberado por assinatura nos planos Mensal, Trimestral ou Anual, com os valores exibidos no momento
          da contratação. O pagamento é feito por cartão de crédito e processado pelo Stripe. A cobrança é antecipada e{" "}
          <strong>a assinatura renova automaticamente</strong> ao fim de cada período, até que você cancele.
        </p>
        <p>
          Os preços podem ser reajustados. Qualquer mudança será avisada por e-mail com pelo menos 30 dias de
          antecedência e só vale a partir da renovação seguinte.
        </p>
      </S>
      <S t="5. Cancelamento">
        <p>
          Você pode cancelar a qualquer momento, sem multa, pela área <strong>Minha conta</strong> do App. O acesso
          continua até o fim do período já pago, e não há devolução proporcional do período em curso, exceto no caso do
          item 6.
        </p>
      </S>
      <S t="6. Direito de arrependimento">
        <p>
          Na primeira contratação, você pode desistir em até 7 (sete) dias contados do pagamento e receber de volta o
          valor integral, nos termos do art. 49 do Código de Defesa do Consumidor. Basta pedir pelo contato abaixo.
        </p>
      </S>
      <S t="7. Falta de pagamento">
        <p>
          Se a cobrança da renovação for recusada, o Stripe tenta de novo por alguns dias. Sem pagamento, o acesso às
          funções do App é suspenso, mas seus dados continuam guardados: você pode renovar, baixar uma cópia ou excluir a
          conta a qualquer momento.
        </p>
      </S>
      <S t="8. Uso permitido">
        <p>
          Não é permitido: compartilhar a conta com terceiros, tentar acessar dados de outros usuários, burlar a
          cobrança ou as proteções do sistema, fazer engenharia reversa, revender o acesso ou usar o App para fins
          ilegais. O descumprimento pode levar à suspensão da conta.
        </p>
      </S>
      <S t="9. Propriedade intelectual">
        <p>
          O App, sua marca, layout e código pertencem aos seus titulares. Os dados que você cadastra são seus, e você
          pode exportá-los quando quiser.
        </p>
      </S>
      <S t="10. Disponibilidade e responsabilidade">
        <p>
          Trabalhamos para manter o App disponível e seguro, mas podem ocorrer interrupções para manutenção ou por falhas
          de terceiros (hospedagem, internet, meios de pagamento). Mantemos backup diário dos dados. Na medida permitida
          pela lei, não respondemos por lucros cessantes ou por decisões tomadas com base em dados informados
          incorretamente.
        </p>
      </S>
      <S t="11. Exclusão da conta">
        <p>
          Você pode excluir sua conta e todos os seus dados pela área Minha conta. A exclusão cancela a assinatura na
          mesma hora e não pode ser desfeita.
        </p>
      </S>
      <S t="12. Alterações destes Termos">
        <p>
          Estes Termos podem ser atualizados. Mudanças relevantes serão avisadas por e-mail ou dentro do App.
        </p>
      </S>
      <S t="13. Lei aplicável e foro">
        <p>
          Aplica-se a lei brasileira.{" "}
          {empresa.cidade_uf
            ? `Fica eleito o foro da comarca de ${empresa.cidade_uf}, ressalvado o direito do consumidor de propor ação no foro do seu domicílio.`
            : "Fica eleito o foro do domicílio do consumidor."}
        </p>
      </S>
      <S t="14. Contato">
        <p>Dúvidas, cancelamentos e pedidos de reembolso: <Contato empresa={empresa} />.</p>
      </S>
    </Layout>
  );
}

export function PrivacidadePage() {
  const empresa = useEmpresa();
  return (
    <Layout titulo="Política de Privacidade">
      <S t="1. Quem cuida dos seus dados">
        <p>
          Esta Política explica como o Aesthetica Pricing Pro trata dados pessoais, conforme a Lei Geral de Proteção de
          Dados (Lei 13.709/2018, LGPD). O controlador dos dados é <Quem empresa={empresa} />. Contato do encarregado:{" "}
          <Contato empresa={empresa} />.
        </p>
      </S>
      <S t="2. Quais dados coletamos">
        <ul className="list-disc space-y-1 pl-6">
          <li><strong>Cadastro:</strong> nome, e-mail e senha (guardada criptografada, ninguém consegue lê-la).</li>
          <li><strong>Assinatura:</strong> plano, situação e datas da assinatura, e o identificador de cliente no Stripe. Os dados do cartão ficam só com o Stripe e nunca passam pelo App.</li>
          <li><strong>Dados que você cadastra:</strong> produtos, insumos, custos, preços, fichas técnicas e configurações.</li>
          <li><strong>Dados técnicos e de segurança:</strong> endereço IP e registros de tentativas de login, usados para bloquear tentativas de invasão.</li>
          <li><strong>Cookies:</strong> de sessão, necessários para manter você logado; e de medição (Meta Pixel e Google Tag Manager), que registram visitas e compras para medir os anúncios.</li>
        </ul>
      </S>
      <S t="3. Para que usamos e com qual base legal">
        <ul className="list-disc space-y-1 pl-6">
          <li>Prestar o serviço, liberar o acesso e cobrar a assinatura: <em>execução de contrato</em>.</li>
          <li>Enviar e-mails de confirmação, de recuperação de senha e avisos sobre a assinatura: <em>execução de contrato</em>.</li>
          <li>Proteger contas, prevenir fraudes e manter backups: <em>legítimo interesse</em>.</li>
          <li>Guardar registros de pagamento: <em>cumprimento de obrigação legal</em>.</li>
          <li>Medir o resultado de anúncios: <em>legítimo interesse</em>, com a possibilidade de você bloquear esses cookies no navegador.</li>
        </ul>
      </S>
      <S t="4. Com quem compartilhamos">
        <p>Não vendemos dados. Compartilhamos apenas o necessário com os fornecedores que fazem o App funcionar:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li><strong>Stripe:</strong> processamento de pagamentos e assinaturas.</li>
          <li><strong>Resend:</strong> envio dos e-mails do App.</li>
          <li><strong>Provedor de hospedagem:</strong> servidores e banco de dados do App.</li>
          <li><strong>Meta e Google:</strong> medição de anúncios, pelos cookies descritos acima.</li>
        </ul>
        <p>Alguns desses fornecedores ficam fora do Brasil. A transferência segue as garantias previstas na LGPD.</p>
      </S>
      <S t="5. Por quanto tempo guardamos">
        <p>
          Enquanto sua conta existir. Se a assinatura vencer, os dados continuam guardados para você renovar ou baixar
          uma cópia. Ao excluir a conta, apagamos na hora seus dados cadastrados e as cópias de backup. Mantemos apenas
          os registros de pagamento exigidos por lei, pelo prazo legal.
        </p>
      </S>
      <S t="6. Seus direitos">
        <p>Você pode, a qualquer momento:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>confirmar quais dados temos e acessá-los;</li>
          <li>corrigir dados incompletos ou errados;</li>
          <li>levar seus dados com você: o botão Exportar Backup gera um arquivo com tudo o que você cadastrou;</li>
          <li>excluir a conta e os dados, pela área Minha conta;</li>
          <li>saber com quem compartilhamos seus dados e revogar consentimentos.</li>
        </ul>
        <p>Para qualquer outro pedido, escreva para <Contato empresa={empresa} />. Respondemos em até 15 dias.</p>
      </S>
      <S t="7. Segurança">
        <p>
          Usamos conexão criptografada (HTTPS), senhas com criptografia forte, separação dos dados de cada cliente,
          bloqueio após tentativas erradas de login, verificação em duas etapas opcional e backup diário.
        </p>
      </S>
      <S t="8. Alterações desta Política">
        <p>Esta Política pode ser atualizada. Mudanças relevantes serão avisadas por e-mail ou dentro do App.</p>
      </S>
    </Layout>
  );
}
